import React from 'react';
import {
  StandardDialog,
  Button,
  FieldLabel,
  AddressDetailsField,
  TextField,
  PhoneField,
} from '@roc/ui';
import { observer } from 'mobx-react';
import { QuoteTypeStore } from 'libs/feature-one-tool/src/quote/stores/quoteTypeStore';
import {
  Grid,
  InputAdornment,
  Typography,
  TextField as MuiTextField,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import {
  TitleFormStore,
  TitleCompanyItem,
} from 'libs/feature-one-tool/src/quote/stores/closing/titleFormStore';
import { TitleContactSection } from 'libs/feature-documents/src/documents/components/documentForms/titleAgentV2/titleContactSection';

interface AddNewOption {
  isAddNew: true;
  name: string;
}

type SearchOption = TitleCompanyItem | AddNewOption;

const baseFilter = createFilterOptions<TitleCompanyItem>({
  matchFrom: 'any',
  stringify: (o) => o.name,
});

const filterOptions = (options: SearchOption[], params: any): SearchOption[] => {
  const filtered = baseFilter(options as TitleCompanyItem[], params);
  if (filtered.length === 0 && params.inputValue.trim()) {
    return [{ isAddNew: true, name: params.inputValue.trim() }];
  }
  return filtered;
};

interface Props {
  store: QuoteTypeStore;
}

export const OtherTitleDialogV2 = observer(({ store }: Props) => {
  const { closingStore } = store;
  const { titleFormStore } = closingStore;

  const nc = titleFormStore.newCompany;
  const isExistingCompany = nc.titleAgencyId !== null;
  const street = [nc.streetNumber, nc.streetName].filter(Boolean).join(' ') || nc.noMatchGoogleAddress || '';

  const canConfirm =
    !!nc.name.trim() &&
    titleFormStore.selectedContactIds.size > 0 &&
    (!titleFormStore.isEditing || titleFormStore.hasChanged);

  return (
    <StandardDialog
      open={titleFormStore.showTitleDialog}
      title="Title"
      maxWidth="md"
      handleClose={() => titleFormStore.closeDialog()}
      dialogContent={
        <Grid container spacing={2}>

          {/* Company name / search */}
          <Grid item xs={12}>
            <FieldLabel>Company name *</FieldLabel>
            <Autocomplete
              style={{ width: '100%' }}
              id="closingTitleCompanySearch"
              options={titleFormStore.titleCompanies as SearchOption[]}
              freeSolo
              loading={titleFormStore.loading}
              filterOptions={filterOptions}
              getOptionLabel={(option: any) => {
                if (typeof option === 'string') return option;
                if (option?.isAddNew) return option.name;
                return option?.name ?? '';
              }}
              inputValue={nc.name}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') {
                  titleFormStore.setSearchText(value);
                } else if (reason === 'clear') {
                  titleFormStore.changeCompany();
                }
              }}
              onChange={(_event, newValue: any) => {
                if (!newValue) return;
                if (typeof newValue === 'string') {
                  titleFormStore.setSearchText(newValue);
                  return;
                }
                if (newValue.isAddNew) {
                  titleFormStore.prefillNewName(newValue.name);
                } else {
                  titleFormStore.selectExistingCompany(newValue as TitleCompanyItem);
                }
              }}
              renderOption={(option: any) => {
                if (option.isAddNew) {
                  return (
                    <Grid container direction="column" style={{ padding: '4px 0' }}>
                      <Typography style={{ color: 'rgba(0,0,0,0.54)', marginBottom: 8 }}>
                        No matching companies
                      </Typography>
                      <Typography color="primary">
                        {`+ Add "${option.name}" as a new title company`}
                      </Typography>
                    </Grid>
                  );
                }
                return (
                  <Grid container direction="column">
                    <Typography variant="body1">{option.name}</Typography>
                    {option.address && (
                      <Typography variant="body2" color="textSecondary">
                        {option.address}
                      </Typography>
                    )}
                  </Grid>
                );
              }}
              renderInput={params => (
                <MuiTextField
                  {...params}
                  variant="outlined"
                  placeholder="Search by company name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Address: read-only for existing, editable for new */}
          {isExistingCompany ? (
            <>
              <Grid item xs={12}>
                <TextField
                  standaloneLabel
                  label="Street number and name"
                  variant="outlined"
                  value={street}
                  fullWidth
                  testId="existingTitleStreet"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  standaloneLabel
                  label="City"
                  variant="outlined"
                  value={nc.city}
                  fullWidth
                  testId="existingTitleCity"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  standaloneLabel
                  label="State"
                  variant="outlined"
                  value={nc.state}
                  fullWidth
                  testId="existingTitleState"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  standaloneLabel
                  label="Zip code"
                  variant="outlined"
                  value={nc.zipCode}
                  fullWidth
                  testId="existingTitleZip"
                  disabled
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <FieldLabel>Company address</FieldLabel>
              <AddressDetailsField
                value={nc.address}
                onChange={value => titleFormStore.setAddressField(value)}
                name="titleCompanyAddress"
                standaloneLabel={false}
                showMap={!nc.address}
              />
            </Grid>
          )}

          {/* Shared contact section */}
          <Grid item xs={12}>
            <TitleContactSection
              contacts={titleFormStore.contacts}
              selectedContactIds={titleFormStore.selectedContactIds}
              onToggle={id => titleFormStore.toggleContact(id)}
              onAddContact={draft => titleFormStore.addContact(draft)}
              onUpdateContact={updated => titleFormStore.updateContact(updated.contactId!, updated)}
              disableEditForPersisted={titleFormStore.isEditing}
            />
          </Grid>

        </Grid>
      }
      dialogActions={
        <>
          <Button
            style={{ marginRight: '8px' }}
            onClick={() => titleFormStore.closeDialog()}
            color="primary"
            variant="outlined"
            testId="cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={() => titleFormStore.saveTitle()}
            disabled={!canConfirm}
            color="primary"
            variant="contained"
            testId="confirm"
          >
            Confirm
          </Button>
        </>
      }
    />
  );
});
